/* eslint-disable camelcase */
/* eslint-disable no-param-reassign */
/* eslint-disable no-useless-escape */
import DOMPurify from 'dompurify';
import { get, groupBy, map } from 'lodash';
import { createUUIDv4 } from '../../util/helpers';
import { OPERATORS } from './tokens';
import AjaxUtils from '../../util/ajax_utils';
import { api } from './api';
import { toastMessage } from '@/components/_ui/_utils/useEventUtils';

export const WEB_APP = { value: 'web_app', label: 'Web Application' };
export const INFRA = { value: 'infrastructure', label: 'Infrastructure' };
export const VULN_TYPES = [WEB_APP, INFRA];
export function getCookie(name) {
  const cookies = document.cookie.split(';').map((e) => e.split('=').map((v) => v.trim()));
  // this is our session auth cookie we need to use for all subsequent backend requests
  var v = cookies.find(([k]) => k === name);
  return v ? v[1] : null;
}

export function toast({ message, type: variant, autoHideDelay, ...props }) {
  toastMessage({
    message,
    type: variant || 'success',
    hideTimeout: autoHideDelay || 3000,
    ...props,
  });
}

export function sanitizeHTML(dirty) {
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: [
      'a',
      'b',
      'code',
      'em',
      'i',
      'ul',
      'li',
      'lo',
      'p',
      'pre',
      'strong',
      'table',
      'tbody',
      'td',
      'th',
      'thead',
      'tr',
    ],
    ALLOWED_ATTR: ['href', 'class', 'target', 'rel'],
    KEEP_CONTENT: true,
  });
}

export function formatDateTime(dt, short) {
  const dtf = dt.toISOString().replace(/T/, ' ').replace(/\..+/, '');
  return short ? dtf.slice(0, 10) : dtf;
}

export function dtFormat(dt) {
  let time = dt;
  switch (typeof time) {
    case 'number':
      break;
    case 'string':
      time = +new Date(time);
      break;
    case 'object':
      if (time.constructor === Date) time = time.getTime();
      break;
    default:
      time = +new Date();
  }
  const dtFormats = [
    [60, 'seconds', 1], // 60
    [120, '1 minute ago', '1 minute from now'], // 60*2
    [3600, 'minutes', 60], // 60*60, 60
    [7200, '1 hour ago', '1 hour from now'], // 60*60*2
    [86400, 'hours', 3600], // 60*60*24, 60*60
    [172800, 'Yesterday', 'Tomorrow'], // 60*60*24*2
    [604800, 'days', 86400], // 60*60*24*7, 60*60*24
    [1209600, 'Last week', 'Next week'], // 60*60*24*7*4*2
    [2419200, 'weeks', 604800], // 60*60*24*7*4, 60*60*24*7
    [4838400, 'Last month', 'Next month'], // 60*60*24*7*4*2
    [29030400, 'months', 2419200], // 60*60*24*7*4*12, 60*60*24*7*4
    [58060800, 'Last year', 'Next year'], // 60*60*24*7*4*12*2
    [2903040000, 'years', 29030400], // 60*60*24*7*4*12*100, 60*60*24*7*4*12
    [5806080000, 'Last century', 'Next century'], // 60*60*24*7*4*12*100*2
    [58060800000, 'centuries', 2903040000], // 60*60*24*7*4*12*100*20, 60*60*24*7*4*12*100
  ];
  let seconds = (+new Date() - time) / 1000;
  let token = 'ago';
  let idx = 1;

  if (seconds === 0) {
    return 'Just now';
  }
  if (seconds < 0) {
    seconds = Math.abs(seconds);
    token = 'from now';
    idx = 2;
  }
  for (let i = 0; i < dtFormats.length; i += 1) {
    const format = dtFormats[i];
    if (seconds < format[0]) {
      if (typeof format[2] === 'string') return format[idx];
      return `${Math.floor(seconds / format[2])} ${format[1]} ${token}`;
    }
  }
  return time;
}

export function formatDatetimeColumn(field) {
  return (v) => `<span title="${v[field]}">${dtFormat(v[field])}</span>`;
}

function encodePathSegment(seg) {
  if (seg == null) return '';
  return encodeURIComponent(String(seg));
}

export function formatImgColumn(field, contentTypeField, title) {
  // Pre-escape the static title once (HTML attribute safe)
  const safeTitle = String(title ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/"/g, '&quot;');

  return (v) => {
    if (!v?.vendor) {
      return '<img class="logo-img-col" src="" />';
    }

    const vendor = encodePathSegment(v.vendor);
    const product = encodePathSegment(v.product);

    const src = `/api/v1/logos/${vendor}/${product}/`;
    return `<img class="logo-img-col" src="${src}" title="${safeTitle}" />`;
  };
}

export function stringIsAValidUrl(s) {
  if (s === '' || s === null) return true;
  if (s === undefined) return false;
  try {
    // eslint-disable-next-line no-new
    new URL(s);
    return true;
  } catch (err) {
    return false;
  }
}

export function toISODate(date) {
  return date.toISOString().slice(0, 10);
}

export function reverseObj(obj) {
  return Object.entries(obj).reduce((prev, curr) => {
    const [key, value] = curr;
    // eslint-disable-next-line no-param-reassign
    prev[value] = key;
    return prev;
  }, {});
}

export function onInputFocus(that) {
  if (!that.target) return;
  that.target.style.height = '';
  that.target.style.height = `${that.target.scrollHeight}px`;
}

export function buildRequestParams(sid, opts = {}) {
  const optsHeaders = opts?.headers || {};
  return {
    ...opts,
    flash_form_errors: false,
    flash_validation_errors: false,
    headers: {
      ...optsHeaders,
      Authorization: `Bearer ${sid}`,
      'X-CSRF-Token': getCookie('csrf_token'),
      // NOTE: this function does not actually create a _proper_ UUIDv4
      // meaning this serves as a random id only; the time info is not encoded.
      'X-Correlation-ID': createUUIDv4(),
    },
  };
}

export function capitalize(str) {
  return str.at(0).toUpperCase() + str.slice(1);
}

export function loadCookies() {
  const sid = getCookie('session');
  return sid ? sid[1] : null;
}

export const SELECT_COLUMN_KEY = 'select';
export const SELECT_COLUMN = {
  id: SELECT_COLUMN_KEY,
  db_field: SELECT_COLUMN_KEY,
  width: 50,
  maxWidth: 50,
  center: false,
  data: 'checked',
  input: true,
  heading: '',
  canSearch: false,
  ctype: 'checkboxcolumn',
};
export const PVV_LOOKUP = { 0: 'product', 1: 'vendor', 2: 'version' };

// Vuln levels, copied from sec1lib/sec1lib/data/vuln.py
export const CRITICAL = '5.0-critical';
export const HIGH = '4.0-high';
export const MEDIUM = '3.0-medium';
export const LOW = '2.0-low';
export const INFO = '1.0-info';
export const INVESTIGATE = '0.0-investigate';
export const UNKNOWN = '0.0-unknown';

export const IMPACT_PROBABILITY_READ_VALUES = {
  [CRITICAL]: 'Critical',
  [HIGH]: 'High',
  [MEDIUM]: 'Medium',
  [LOW]: 'Low',
  [INFO]: 'Info',
  [INVESTIGATE]: 'Investigate',
  [UNKNOWN]: 'Unknown',
};

export function escapeSQLPatternCharacters(str) {
  // escapes common SQL pattern characters to turn them into literals for filtering purposes
  return str
    .replace(/\\/g, '\\\\')
    .replace(/\$/g, '\\$')
    .replace(/\_/g, '\\_')
    .replace(/\%/g, '\\%')
    .replace(/'/g, "\\'")
    .replace(/"/g, '\\"');
}

export function parseOp(value) {
  if (!value) return null;
  const { data, operator } = value;
  let op = OPERATORS[operator];
  // eslint-disable-next-line no-nested-ternary
  op = data !== 'None' && data !== 'null' ? op : op === 'eq' ? 'is_null' : 'is_not_null';
  return op;
}

export function baseParseFilters(data, pvv = true) {
  // this function's aim is to process the filters from the search-bar format
  // into the format suitable for the backend.
  // however, handling all the edge-cases has slowly made it quite complex
  // still, the general logic is:
  // - rename columns as needed for the backend
  // - parse empty strings, quotes
  // - make sure the ops get parsed correctly
  // - handle arrays being pased through
  // - change OP to is_null/is_not_null for relevant comparisons to null values
  // - skip "special" filters that get processed outside the normal ones
  //   (e.g. product, version, vendor)
  // - group filters of the same type
  // - handle SQL escape characters
  const filters = [];
  const search = [];
  const pvvQuery = [false, false, false];
  if (!data) data = [];
  data.forEach((e) => {
    if (typeof e === 'string') {
      search.push(e);
      return;
    }
    if (!e?.value) return;
    let field;
    let value;
    let skip;
    skip = false;
    let op = parseOp(e.value);
    if (e.value.data === false || e.value.data === true) {
      // booleans; leave as is
      value = e.value.data;
      // eslint-disable-next-line eqeqeq
    } else if (
      // eslint-disable-next-line eqeqeq
      e.value.data == '""' ||
      // eslint-disable-next-line eqeqeq
      e.value.data == '' ||
      // eslint-disable-next-line eqeqeq
      e.value.data == '\'"'
    ) {
      e.value.data = '';
    }
    field = e.type;
    let prefix;
    switch (e.type) {
      case 'product':
        value = e.value.data;
        skip = pvv;
        pvvQuery[0] = true;
        break;
      case 'vendor':
        value = e.value.data;
        skip = pvv;
        pvvQuery[1] = true;
        break;
      case 'version':
        value = e.value.data;
        skip = pvv;
        pvvQuery[2] = true;
        break;
      case 'created_by':
        // usernames
        field = 'created_by_fk';
        value = e.value.data.split(',');
        break;
      case 'changed_by':
        // usernames
        field = 'changed_by_fk';
        value = e.value.data.split(',');
        break;
      case 'feed_name':
        field = 'feed.name';
        value = e.value.data;
        break;
      case 'cwes':
        field = 'cwes.id';
        value = op === 'in' || op === 'not_in' ? e.value.data.split(',') : e.value.data;
        break;
      case 'patches':
        field = 'patches.id';
        value = op === 'in' || op === 'not_in' ? e.value.data.split(',') : e.value.data;
        if (value.length === 1 && value[0] === '') skip = true;
        break;
      case 'cves':
        field = 'cves.id';
        value = op === 'in' || op === 'not_in' ? e.value.data.split(',') : e.value.data;
        break;
      case 'category':
        field = 'category';
        value = e.value.data.replace(/['"]+/g, '').replace(/ /g, '+');
        break;
      case 'type':
        field = 'type';
        value = e.value.data;
        break;
      case 'affected.id':
        field = 'affected.id';
        value = e.value.data;
        prefix = value !== '0' ? 'count_distinct__' : 'count__';
        op = `${prefix}${op}`;
        break;
      case 'fixed.id':
        field = 'fixed.id';
        value = e.value.data;
        prefix = value !== '0' ? 'count_distinct__' : 'count__';
        op = `${prefix}${op}`;
        break;
      case 'cwes.id':
        field = 'cwes.id';
        value = e.value.data;
        prefix = value !== '0' ? 'count_distinct__' : 'count__';
        op = `${prefix}${op}`;
        break;
      case 'is_junk':
        field = 'is_junk';
        value = e.value.data;
        op = value === 'true' ? 'is_not_null' : 'is_null';
        break;
      default:
        field = e.type;
        value = e.value.data;
        break;
    }
    // if we are querying for a .id field (i.e. a number field)
    // we can drop this early if the value includs non-digit characters
    const isValueString =
      typeof value === 'string' &&
      value !== 'null' &&
      (value.match(/[^\d]+/) || value === '""' || value === '');
    if (
      (field.includes('.id') || field.includes('score') || field.includes('forked')) &&
      isValueString
    ) {
      // some hardcoded tokens we can attempt to fix
      if (field === 'cves.id') {
        field = 'cves.cve';
        op = 'ilike';
      } else if (field === 'cwes.id') {
        field = 'cwes.cwe';
        op = 'ilike';
      } else if (field === 'cpes.id') {
        field = 'cpes.cpe';
        op = 'ilike';
      } else if (field.includes('score') && value === 'null') {
        op = op === 'eq' ? 'is_null' : 'is_not_null';
      } else if (field.includes('forked') && value === 'null') {
        // op = value === 'null' ? 'is_null' : 'is_not_null';
        op = op === 'eq' ? 'is_null' : 'is_not_null';
        // value = 'null';
      } else {
        return;
      }
    }
    let cast;
    if (
      [
        'conditions',
        'vendor',
        'product',
        'version',
        'update',
        'target_sw',
        'edition',
        'sw_edition',
        'hw_edition',
        'language',
        'other',
      ].includes(field)
    ) {
      cast = 'string';
    }
    if (typeof value === 'string') value = value.replace(/\"/g, '');
    if ((op === 'in' || op === 'not_in') && Array.isArray(value) && value.includes('null')) {
      value = value.filter((x) => x !== 'null');
      filters.push({
        field,
        op: 'is_null',
        cast,
      });
    }
    if (!skip) {
      filters.push({
        field,
        op,
        value,
        cast,
      });
    }
  });
  return { filters, search: search.join(''), pvvQuery };
}

export const PROBABILITY_IMPACT_OPTIONS = [
  {
    id: 7,
    title: 'Critical',
    value: '5.0-critical',
    name: '5.0-critical',
    color: '#d90429',
    text_color: '#ffffff',
  },
  {
    id: 6,
    title: 'High',
    value: '4.0-high',
    name: '4.0-high',
    color: '#fb5607',
    text_color: '#ffffff',
  },
  {
    id: 5,
    title: 'Medium',
    value: '3.0-medium',
    name: '3.0-medium',
    color: '#ffbe0b',
    text_color: '#333333',
  },
  {
    id: 4,
    title: 'Low',
    value: '2.0-low',
    name: '2.0-low',
    color: '#5CB85C',
    text_color: '#ffffff',
  },
  {
    id: 3,
    title: 'Informational',
    value: '1.0-info',
    name: '1.0-info',
    color: '#faedcd',
    text_color: '#333333',
  },
  {
    id: 2,
    title: 'Investigate',
    value: '0.0-investigate',
    name: '0.0-investigate',
    color: '#edede9',
    text_color: '#333333',
  },
  {
    id: 1,
    title: 'Unknown',
    value: '0.0-unknown',
    name: '0.0-unknown',
    color: '#dede9e',
    text_color: '#333333',
  },
];

const NOT_IMPLEMENTED = 0;
const TERRIBLE = 10;
const BAD = 20;
const OK = 30;
const GOOD = 40;
const GREAT = 50;

const NEW = 'new';
const MODIFIED = 'modified';
const IGNORE = 'ignore';
const HANDLED = 'handled';
const DEPRECATED = 'deprecated';
const APPROVED = 'approved';

export const WORKFLOW_STATUSES = [
  { value: NEW, label: 'New' },
  { value: MODIFIED, label: 'Modified' },
  { value: APPROVED, label: 'Approved' },
  { value: IGNORE, label: 'Ignore' },
  { value: HANDLED, label: 'Handled' },
  { value: DEPRECATED, label: 'Deprecated' },
];

export const CWE_STATUSES = {
  ALLOWED: 'ALLOWED',
  DISCOURAGED: 'DISCOURAGED',
  PROHIBITED: 'PROHIBITED',
};

export const CWE_STATUS_OPTIONS = [
  {
    value: CWE_STATUSES.ALLOWED,
    label: 'Allowed',
  },
  {
    value: CWE_STATUSES.DISCOURAGED,
    label: 'Discouraged',
  },
  {
    value: CWE_STATUSES.PROHIBITED,
    label: 'Prohibited',
  },
];

export const makeOptions = (obj, valueName = 'value', labelName = 'label') =>
  Object.entries(obj).map((e) => ({
    [valueName]: e[0],
    [labelName]: e[1],
  }));

export const WORKFLOW_QUALITIES = [
  { value: NOT_IMPLEMENTED, label: 'Not Implemented' },
  { value: TERRIBLE, label: 'Terrible' },
  { value: BAD, label: 'Bad' },
  { value: OK, label: 'OK' },
  { value: GOOD, label: 'Good' },
  { value: GREAT, label: 'Great' },
];

export const OWASP2013 = {
  1: 'A1 - Injection',
  2: 'A2 - Broken Authentication and Session Management',
  3: 'A3 - Cross-Site Scripting (XSS)',
  4: 'A4 - Insecure Direct Object References',
  5: 'A5 - Security Misconfiguration',
  6: 'A6 - Sensitive Data Exposure',
  7: 'A7 - Missing Function Level Access Control',
  8: 'A8 - Cross-Site Request Forgery (CSRF)',
  9: 'A9 - Using Known Vulnerable Components',
  10: 'A10 - Unvalidated Redirects and Forwards',
};

export const OWASP2017 = {
  1: 'A1 - Injection',
  2: 'A2 - Broken Authentication',
  3: 'A3 - Sensitive Data Exposure',
  4: 'A4 - XML External Entities (XXE)',
  5: 'A5 - Broken Access Control',
  6: 'A6 - Security Misconfiguration',
  7: 'A7 - Cross-Site Scripting (XSS)',
  8: 'A8 - Insecure Deserialization',
  9: 'A9 - Using Components with Known Vulnerabilities',
  10: 'A10 - Insufficient Logging & Monitoring',
};

export const OWASP2021 = {
  1: 'A1 - Broken Access Control',
  2: 'A2 - Cryptographic Failures',
  3: 'A3 - Injection',
  4: 'A4 - Insecure Design',
  5: 'A5 - Security Misconfiguration',
  6: 'A6 - Vulnerable and Outdated Components',
  7: 'A7 - Identification and Authentication Failures',
  8: 'A8 - Software and Data Integrity Failures',
  9: 'A9 - Security Logging and Monitoring Failures',
  10: 'A10 - Server-Side Request Forgery',
};

export const OWASP2025 = {
    1: 'A01 - Broken Access Control',
    2: 'A02 - Security Misconfiguration',
    3: 'A03 - Software Supply Chain Failures',
    4: 'A04 - Cryptographic Failures',
    5: 'A05 - Injection',
    6: 'A06 - Insecure Design',
    7: 'A07 - Authentication Failures',
    8: 'A08 - Software or Data Integrity Failures',
    9: 'A09 - Security Logging and Alerting Failures',
    10: 'A10 - Mishandling of Exceptional Conditions',
};

export const AssocCols = [
  {
    id: 'part',
    field: 'Part',
    db_field: 'cpe.part',
    data: 'part',
    heading: 'Part',
    noSort: true,
  },
  {
    id: 'vendor',
    field: 'Vendor',
    db_field: 'cpe.vendor',
    data: 'vendor',
    heading: 'Vendor',
  },
  {
    id: 'product',
    field: 'Product',
    db_field: 'cpe.product',
    data: 'product',
    heading: 'Product',
  },
  {
    id: 'version',
    field: 'Version',
    db_field: 'cpe.version',
    data: 'version',
    heading: 'Version',
  },
  {
    id: 'update',
    field: 'Update',
    db_field: 'cpe.update',
    data: 'update',
    heading: 'Update',
    noSort: true,
  },
  {
    id: 'edition',
    field: 'Edition',
    db_field: 'cpe.edition',
    data: 'edition',
    heading: 'Edition',
    noSort: true,
  },
  {
    id: 'language',
    field: 'Language',
    db_field: 'cpe.language',
    data: 'language',
    heading: 'Language',
    noSort: true,
  },
  {
    id: 'sw_edition',
    field: 'SW Edition',
    db_field: 'cpe.sw_edition',
    data: 'sw_edition',
    heading: 'SW Edition',
    noSort: true,
  },
  {
    id: 'target_sw',
    field: 'Target SW',
    db_field: 'cpe.target_sw',
    data: 'target_sw',
    heading: 'Target SW',
    noSort: true,
  },
  {
    id: 'target_hw',
    field: 'Target HW',
    db_field: 'cpe.target_hw',
    data: 'target_hw',
    heading: 'Target HW',
    noSort: true,
  },
  {
    id: 'other',
    field: 'Other',
    db_field: 'cpe.other',
    data: 'other',
    heading: 'Other',
    noSort: true,
  },
  {
    id: 'cpe_id',
    field: 'CPE ID',
    db_field: 'cpe.id',
    data: 'cpe_id',
    heading: 'CPE ID',
    canSearch: true,
  },
  {
    id: 'assoc_id',
    field: 'Assoc ID',
    db_field: 'id',
    data: 'assoc_id',
    heading: 'Assoc ID',
    canSearch: true,
    noSort: true,
  },
  {
    id: 'first',
    field: 'First',
    db_field: 'first',
    data: 'first',
    heading: 'First',
    canSearch: true,
  },
  {
    id: 'last',
    field: 'Last',
    db_field: 'last',
    data: 'last',
    heading: 'Last',
    canSearch: true,
  },
  {
    id: 'source_type',
    field: 'Source Type',
    db_field: 'source_type',
    data: 'source_type',
    heading: 'Source Type',
    canSearch: true,
  },
];

export const FixedCols = [
  {
    id: 'part',
    field: 'Part',
    db_field: 'cpe.part',
    data: 'part',
    heading: 'Part',
    noSort: true,
  },
  {
    id: 'vendor',
    field: 'Vendor',
    db_field: 'cpe.vendor',
    data: 'vendor',
    heading: 'Vendor',
  },
  {
    id: 'product',
    field: 'Product',
    db_field: 'cpe.product',
    data: 'product',
    heading: 'Product',
  },
  {
    id: 'version',
    field: 'Version',
    db_field: 'cpe.version',
    data: 'version',
    heading: 'Version',
  },
  {
    id: 'update',
    field: 'Update',
    db_field: 'cpe.update',
    data: 'update',
    heading: 'Update',
    noSort: true,
  },
  {
    id: 'edition',
    field: 'Edition',
    db_field: 'cpe.edition',
    data: 'edition',
    heading: 'Edition',
    noSort: true,
  },
  {
    id: 'language',
    field: 'Language',
    db_field: 'cpe.language',
    data: 'language',
    heading: 'Language',
    noSort: true,
  },
  {
    id: 'sw_edition',
    field: 'SW Edition',
    db_field: 'cpe.sw_edition',
    data: 'sw_edition',
    heading: 'SW Edition',
    noSort: true,
  },
  {
    id: 'target_sw',
    field: 'Target SW',
    db_field: 'cpe.target_sw',
    data: 'target_sw',
    heading: 'Target SW',
    noSort: true,
  },
  {
    id: 'target_hw',
    field: 'Target HW',
    db_field: 'cpe.target_hw',
    data: 'target_hw',
    heading: 'Target HW',
    noSort: true,
  },
  {
    id: 'other',
    field: 'Other',
    db_field: 'cpe.other',
    data: 'other',
    heading: 'Other',
    noSort: true,
  },
  {
    id: 'cpe_id',
    field: 'CPE ID',
    db_field: 'cpe.id',
    data: 'cpe_id',
    heading: 'CPE ID',
    canSearch: true,
  },
  {
    id: 'assoc_id',
    field: 'Assoc ID',
    db_field: 'id',
    data: 'assoc_id',
    heading: 'Assoc ID',
    canSearch: true,
    noSort: true,
  },
  {
    id: 'recommended',
    field: 'Recommended',
    db_field: 'recommended',
    data: 'recommended',
    heading: 'Recommended',
    canSearch: true,
  },
  {
    id: 'source_type',
    field: 'Source Type',
    db_field: 'source_type',
    data: 'source_type',
    heading: 'Source Type',
    canSearch: true,
  },
];

export const CPECols = [
  {
    id: 'id',
    field: 'ID',
    db_field: 'id',
    data: 'db_id',
    heading: 'ID',
    width: 120,
  },
  {
    id: 'title',
    field: 'Title',
    db_field: 'title',
    data: 'title',
    heading: 'Title',
  },
  {
    id: 'part',
    field: 'Part',
    db_field: 'part',
    data: 'part',
    heading: 'Part',
    noSort: true,
  },
  {
    id: 'vendor',
    field: 'Vendor',
    db_field: 'vendor',
    data: 'vendor',
    heading: 'Vendor',
  },
  {
    id: 'product',
    field: 'Product',
    db_field: 'product',
    data: 'product',
    heading: 'Product',
  },
  {
    id: 'version',
    field: 'Version',
    db_field: 'version',
    data: 'version',
    heading: 'Version',
  },
  {
    id: 'update',
    field: 'Update',
    db_field: 'update',
    data: 'update',
    heading: 'Update',
    noSort: true,
  },
  {
    id: 'edition',
    field: 'Edition',
    db_field: 'edition',
    data: 'edition',
    heading: 'Edition',
    noSort: true,
  },
  {
    id: 'language',
    field: 'Language',
    db_field: 'language',
    data: 'language',
    heading: 'Language',
    noSort: true,
  },
  {
    id: 'sw_edition',
    field: 'SW Edition',
    db_field: 'sw_edition',
    data: 'sw_edition',
    heading: 'SW Edition',
    noSort: true,
  },
  {
    id: 'target_sw',
    field: 'Target SW',
    db_field: 'target_sw',
    data: 'target_sw',
    heading: 'Target SW',
    noSort: true,
  },
  {
    id: 'target_hw',
    field: 'Target HW',
    db_field: 'target_hw',
    data: 'target_hw',
    heading: 'Target HW',
    noSort: true,
  },
  {
    id: 'other',
    field: 'Other',
    db_field: 'other',
    data: 'other',
    heading: 'Other',
    noSort: true,
  },
  {
    id: 'deprecated',
    field: 'Deprecated',
    db_field: 'deprecated',
    data: 'deprecated',
    heading: 'Deprecated',
    noSort: true,
  },
  {
    id: 'source_type',
    field: 'Source Type',
    db_field: 'source_type',
    data: 'source_type',
    heading: 'Source Type',
    noSort: true,
  },
];

export const CPEAliasCols = [
  {
    id: 'id',
    field: 'ID',
    db_field: 'id',
    data: 'db_id',
    heading: 'ID',
    width: 120,
  },
  {
    id: 'match_part',
    field: 'Match Part',
    db_field: 'match_part',
    data: 'match_part',
    heading: 'Match Part',
    noSort: true,
  },
  {
    id: 'match_vendor',
    field: 'Match Vendor',
    db_field: 'match_vendor',
    data: 'match_vendor',
    heading: 'Match Vendor',
  },
  {
    id: 'match_product',
    field: 'Match Product',
    db_field: 'match_product',
    data: 'match_product',
    heading: 'Match Product',
  },
  {
    id: 'match_version',
    field: 'Match Version',
    db_field: 'match_version',
    data: 'match_version',
    heading: 'Match Version',
  },
  {
    id: 'match_update',
    field: 'Match Update',
    db_field: 'match_update',
    data: 'match_update',
    heading: 'Match Update',
    noSort: true,
  },
  {
    id: 'match_edition',
    field: 'Match Edition',
    db_field: 'match_edition',
    data: 'match_edition',
    heading: 'Match Edition',
    noSort: true,
  },
  {
    id: 'match_language',
    field: 'Match Language',
    db_field: 'match_language',
    data: 'match_language',
    heading: 'Match Language',
    noSort: true,
  },
  {
    id: 'match_sw_edition',
    field: 'Match SW Edition',
    db_field: 'match_sw_edition',
    data: 'match_sw_edition',
    heading: 'Match SW Edition',
    noSort: true,
  },
  {
    id: 'match_target_sw',
    field: 'Match Target SW',
    db_field: 'match_target_sw',
    data: 'match_target_sw',
    heading: 'Match Target SW',
    noSort: true,
  },
  {
    id: 'match_target_hw',
    field: 'Match Target HW',
    db_field: 'match_target_hw',
    data: 'match_target_hw',
    heading: 'Match Target HW',
    noSort: true,
  },
  {
    id: 'match_other',
    field: 'Match Other',
    db_field: 'match_other',
    data: 'match_other',
    heading: 'Match Other',
    noSort: true,
  },
  {
    id: 'override_part',
    field: 'Ovr. Part',
    db_field: 'override_part',
    data: 'override_part',
    heading: 'Ovr. Part',
    noSort: true,
  },
  {
    id: 'override_vendor',
    field: 'Ovr. Vendor',
    db_field: 'override_vendor',
    data: 'override_vendor',
    heading: 'Ovr. Vendor',
  },
  {
    id: 'override_product',
    field: 'Ovr. Product',
    db_field: 'override_product',
    data: 'override_product',
    heading: 'Ovr. Product',
  },
  {
    id: 'override_version',
    field: 'Ovr. Version',
    db_field: 'override_version',
    data: 'override_version',
    heading: 'Ovr. Version',
  },
  {
    id: 'override_update',
    field: 'Ovr. Update',
    db_field: 'override_update',
    data: 'override_update',
    heading: 'Ovr. Update',
    noSort: true,
  },
  {
    id: 'override_edition',
    field: 'Ovr. Edition',
    db_field: 'override_edition',
    data: 'override_edition',
    heading: 'Ovr. Edition',
    noSort: true,
  },
  {
    id: 'override_language',
    field: 'Ovr. Language',
    db_field: 'override_language',
    data: 'override_language',
    heading: 'Ovr. Language',
    noSort: true,
  },
  {
    id: 'override_sw_edition',
    field: 'Ovr. SW Edition',
    db_field: 'override_sw_edition',
    data: 'override_sw_edition',
    heading: 'Ovr. SW Edition',
    noSort: true,
  },
  {
    id: 'override_target_sw',
    field: 'Ovr. Target SW',
    db_field: 'override_target_sw',
    data: 'override_target_sw',
    heading: 'Ovr. Target SW',
    noSort: true,
  },
  {
    id: 'override_target_hw',
    field: 'Ovr. Target HW',
    db_field: 'override_target_hw',
    data: 'override_target_hw',
    heading: 'Ovr. Target HW',
    noSort: true,
  },
  {
    id: 'override_other',
    field: 'Ovr. Other',
    db_field: 'override_other',
    data: 'override_other',
    heading: 'Ovr. Other',
    noSort: true,
  },
];

export const EUVDCols = [
  {
    id: 'id',
    field: 'ID',
    db_field: 'id',
    data: 'db_id',
    heading: 'ID',
    width: 150,
  },
  {
    id: 'euvd',
    field: 'EUVD',
    db_field: 'euvd',
    data: 'euvd',
    heading: 'EUVD',
  },
];

export const CVECols = [
  {
    id: 'id',
    field: 'ID',
    db_field: 'id',
    data: 'db_id',
    heading: 'ID',
    width: 150,
  },
  {
    id: 'cve',
    field: 'CVE',
    db_field: 'cve',
    data: 'cve',
    heading: 'CVE',
  },
  {
    id: 'epss_score',
    field: 'EPSS Score',
    db_field: 'epss_score',
    data: 'epss_score',
    heading: 'EPSS Score',
  },
  {
    id: 'epss_percentile',
    field: 'EPSS Percentile',
    db_field: 'epss_percentile',
    data: 'epss_percentile',
    heading: 'EPSS Percentile',
  },
];

export const CWECols = [
  {
    id: 'id',
    field: 'ID',
    db_field: 'id',
    data: 'db_id',
    heading: 'ID',
    width: 150,
  },
  {
    id: 'cwe',
    field: 'CWE',
    db_field: 'cwe',
    data: 'cwe',
    heading: 'CWE',
    width: 170,
  },
  {
    id: 'short_name',
    field: 'Short Name',
    db_field: 'short_name',
    data: 'short_name',
    heading: 'Short Name',
  },
  {
    id: 'name',
    field: 'Name',
    db_field: 'name',
    data: 'name',
    heading: 'Name',
  },
  {
    id: 'description',
    field: 'Description',
    db_field: 'description',
    data: 'description',
    heading: 'Description',
  },
  {
    id: 'assigned_category',
    field: 'Category',
    db_field: 'assigned_category',
    data: 'assigned_category',
    heading: 'Category',
  },
  {
    id: 'assigned_owasp_2013',
    field: 'OWASP 2013',
    db_field: 'assigned_owasp_2013',
    data: 'assigned_owasp_2013',
    heading: 'OWASP 2013',
  },
  {
    id: 'assigned_owasp_2017',
    field: 'OWASP 2017',
    db_field: 'assigned_owasp_2017',
    data: 'assigned_owasp_2017',
    heading: 'OWASP 2017',
  },
  {
    id: 'assigned_owasp_2021',
    field: 'OWASP 2021',
    db_field: 'assigned_owasp_2021',
    data: 'assigned_owasp_2021',
    heading: 'OWASP 2021',
  },
  {
    id: 'status',
    field: 'Status',
    db_field: 'status',
    data: 'status',
    heading: 'Status',
  },
  {
    id: 'vulnerability_suffix',
    field: 'Vulnerability Suffix',
    db_field: 'vulnerability_suffix',
    data: 'vulnerability_suffix',
    heading: 'Vulnerability Suffix',
  },
];

export const VendorRefCols = [
  {
    id: 'id',
    field: 'ID',
    db_field: 'id',
    data: 'db_id',
    heading: 'ID',
    width: 150,
  },
  {
    id: 'reference',
    field: 'Reference',
    db_field: 'reference',
    data: 'reference',
    heading: 'Reference',
    width: 170,
  },
  {
    id: 'source',
    field: 'Source',
    db_field: 'source',
    data: 'source',
    heading: 'Source',
    width: 170,
  },
];

export const RawVulnCols = [
  {
    id: 'db_id',
    field: 'ID',
    db_field: 'db_id',
    data: 'db_id',
    heading: 'ID',
    width: 150,
  },
  {
    id: 'title',
    field: 'Title',
    db_field: 'title',
    data: 'title',
    heading: 'Title',
  },
  {
    id: 'description',
    field: 'Description',
    db_field: 'description',
    data: 'description',
    heading: 'Description',
  },
  {
    id: 'feed_id',
    field: 'Feed',
    db_field: 'feed.name',
    data: 'feed.name',
    heading: 'Feed Name',
    width: 150,
  },
  {
    id: 'changed_on',
    field: 'Changed On',
    db_field: 'changed_on',
    data: 'changed_on',
    heading: 'Changed On',
    width: 150,
  },
  {
    id: 'created_on',
    field: 'Created On',
    db_field: 'created_on',
    data: 'created_on',
    heading: 'Created On',
    width: 150,
  },
];

export const PatchCols = [
  {
    id: 'id',
    field: 'ID',
    db_field: 'id',
    data: 'id',
    heading: 'ID',
    width: 150,
  },
  {
    id: 'title',
    field: 'Title',
    db_field: 'title',
    data: 'title',
    heading: 'Title',
  },
  // The Patch descriptions are too long anyway. Let's not display those.
  // {
  //     id: 'description',
  //     field: 'Description',
  //     db_field: 'description',
  //     data: 'description',
  //     heading: 'Description',
  // },
  {
    id: 'patch_id',
    field: 'Patch ID',
    db_field: 'patch_id',
    data: 'patch_id',
    heading: 'Patch ID',
    width: 150,
  },
  {
    id: 'release_date',
    field: 'Release Date',
    db_field: 'release_date',
    data: 'release_date',
    heading: 'Release Date',
    width: 150,
  },
];

export const VulnDetectionCols = [
  {
    id: 'db_id',
    field: 'ID',
    db_field: 'db_id',
    data: 'db_id',
    heading: 'ID',
    width: 150,
  },
  {
    id: 'scanner',
    field: 'Scanner',
    db_field: 'scanner',
    data: 'scanner',
    heading: 'Scanner',
  },
  {
    id: 'plugin_name',
    field: 'Plugin Name',
    db_field: 'plugin_name',
    data: 'plugin_name',
    heading: 'Plugin Name',
  },
  {
    id: 'plugin_id',
    field: 'Plugin ID',
    db_field: 'plugin_id',
    data: 'plugin_id',
    heading: 'Plugin ID',
  },
  {
    id: 'authenticated',
    field: 'Authenticated',
    db_field: 'authenticated',
    data: 'authenticated',
    heading: 'Authenticated',
    width: 150,
  },
  {
    id: 'unauthenticated',
    field: 'Release Date',
    db_field: 'unauthenticated',
    data: 'unauthenticated',
    heading: 'Release Date',
    width: 150,
  },
];

export function generateCPEAssocActions(
  that,
  url,
  field,
  deleteUrl,
  refName,
  dst,
  getQuery,
  noClone,
  deleteSuccessFn,
  modalDisplayInfo
) {
  if (!field) {
    // eslint-disable-next-line no-param-reassign
    field = 'db_id';
  }
  if (!getQuery) {
    getQuery = (row) => ({
      src_id: row?.data?.cpe_id,
      assoc_id: row?.data?.assoc_id,
    });
  }
  const actions = [
    {
      id: 1,
      title: 'Delete',
      icon: 'fa-trash',
      class: 'btn-negative',
      handler(row) {
        const vid = row.get(field);
        const apiUrl = `${that.url}/${deleteUrl}/${vid}/`;
        const fn = that.requestOptsFunc;
        that.$dialog.confirm('Are you sure you want to delete this record?', 'Delete', async () => {
          try {
            const r = await api.delete(apiUrl);
            if (!r.ok) throw new Error(await r.json());
            const data = await r.json();
            // that.$dialog.alert('Record deleted successfully', 'Success');
            // eslint-disable-next-line no-underscore-dangle
            const table_ = that.$refs[refName];
            const table = table_ ? table_.$refs[table_?.name] : null;
            if (table) {
              // eslint-disable-next-line max-len
              table.selectedRows = table.selectedRows.filter((e) => e !== vid);
            }
            that.$emit('table:delete', data);
            if (deleteSuccessFn) {
              deleteSuccessFn(data);
            }
          } catch (e) {
            const { status } = e;
            if (status !== 200) {
              that.$dialog.alert(
                `Something went wrong when trying to delete the record. Received status: ${status}`
              );
            } else {
              that.$dialog.alert('Record deleted successfully', 'Success');
            }
          } finally {
            // refresh the table
            const table = that.$refs[refName];
            if (table) {
              const store = table.$refs[table?.name]?.store;
              if (store) store.load(store.lastParams);
            }
          }
        });
      },
    },
    {
      id: 2,
      title: 'Edit',
      icon: 'fa-edit',
      class: 'btn-primary',
      handler(row, click, direction) {
        const vid = row.get(field);
        window.open(`/${url}/edit/${vid}`, '_blank');
      },
    },
    {
      id: 3,
      title: 'Clone Record',
      icon: 'fa-copy',
      class: 'btn-info',
      handler(row, click, direction) {
        const vid = row.get(field);
        const { href: cloneUrl } = that.$router.resolve({
          name: dst,
          query: getQuery(row),
          target: '_blank',
        });
        window.open(cloneUrl, '_blank');
        return;
      },
    },
    {
      id: 4,
      title: 'Show Record',
      icon: 'fa-eye',
      class: 'btn-info',
      handler(row, click, direction) {
        const vid = row.get(field);
        window.open(`/${url}/show/${vid}`, '_blank');
      },
    },
    {
      id: 5,
      title: 'Add DisplayInfo',
      icon: 'fa-tv',
      class: 'btn-info',
      handler(row, click, direction) {
        that.$lightbox.open({
          title: modalDisplayInfo.title,
          bind: modalDisplayInfo.bind(row),
          component: modalDisplayInfo.component,
          size: modalDisplayInfo.size,
        });
      },
    },
  ];
  return actions;
}

export function generateVulnShowActions(
  that,
  url,
  field,
  modalView,
  modalEdit,
  deleteUrl,
  refName
) {
  if (!field) {
    // eslint-disable-next-line no-param-reassign
    field = 'db_id';
  }
  const actions = [
    {
      id: 2,
      title: 'Edit',
      icon: 'fa-edit',
      class: 'btn-primary',
      handler(row, click, direction) {
        const vid = row.get(field);
        if (!modalEdit || direction === 'middle') {
          window.open(`/${url}/edit/${vid}`, '_blank');
          return;
        }
        this.$lightbox.open({
          title: modalEdit.title,
          bind: modalEdit.bind(row),
          component: modalEdit.component,
          size: modalEdit.size,
        });
      },
    },
    {
      id: 3,
      title: 'Show Record',
      icon: 'fa-eye',
      class: 'btn-info',
      handler(row, click, direction) {
        const vid = row.get(field);
        if (!modalView || direction === 'middle') {
          window.open(`/${url}/show/${vid}`, '_blank');
          return;
        }
        this.$lightbox.open({
          title: modalView.title,
          bind: modalView.bind(row),
          component: modalView.component,
          size: modalView.size,
        });
      },
    },
  ];
  if (deleteUrl && refName) {
    return [
      {
        id: 1,
        title: 'Delete',
        icon: 'fa-trash',
        class: 'btn-negative',
        handler(row) {
          const vid = row.get(field);
          const apiUrl = `${that.url}/${deleteUrl}/${vid}/`;
          const fn = that.requestOptsFunc;

          that.$dialog.confirm(
            'Are you sure you want to delete this record?',
            'Delete',
            async () => {
              try {
                await api.delete(apiUrl);
                that.$dialog.alert('Record deleted successfully', 'Success');
                // eslint-disable-next-line no-underscore-dangle
                const table_ = that.$refs[refName];
                const table = table_ ? table_.$refs[table_?.name] : null;
                if (table) {
                  // eslint-disable-next-line max-len
                  table.selectedRows = table.selectedRows.filter((e) => e !== vid);
                }
              } catch ({ status }) {
                if (status !== 200) {
                  that.$dialog.alert(
                    `Something went wrong when trying to delete the record. Received status: ${status}`
                  );
                } else {
                  that.$dialog.alert('Record deleted successfully', 'Success');
                }
              } finally {
                // refresh the table
                const table = that.$refs[refName];
                if (table) {
                  const store = table.$refs[table?.name]?.store;
                  if (store) store.load(store.lastParams);
                }
              }
            }
          );
        },
      },
      ...actions,
    ];
  }
  return actions;
}

export function generateAssocActions(
  that,
  url,
  field = 'db_id',
  modalView = null,
  modalEdit = null,
  deleteUrl,
  refName
) {
  const arr = [
    {
      id: 3,
      title: 'Show Record',
      icon: 'fa-eye',
      class: 'btn-info',
      handler(row, click, direction) {
        const vid = row.get(field);
        if (!modalView || direction === 'middle') {
          window.open(`/${url}/show/${vid}`, '_blank');
          return;
        }
        that.$lightbox.open({
          title: modalView.title,
          bind: modalView.bind(row),
          component: modalView.component,
          size: modalView.size,
        });
      },
    },
  ];
  if (that.$isEditor()) {
    arr.push({
      id: 2,
      title: 'Edit',
      icon: 'fa-edit',
      class: 'btn-primary',
      handler(row, click, direction) {
        const vid = row.get(field);
        if (!modalEdit || direction === 'middle') {
          // navigate in this tab. push onto history stack to respect browser back button.
          that.$router
            .push({
              path: `/${url}/edit/${vid}`,
            })
            .catch((error) => {
              if (error.name !== 'NavigationDuplicated') {
                throw error;
              }
            });
          // window.open(`/${url}/edit/${vid}`, '_blank');
          return;
        }
        that.$lightbox.open({
          title: modalEdit.title,
          bind: modalEdit.bind(row),
          component: modalEdit.component,
          size: modalEdit.size,
        });
      },
    });
    arr.push({
      id: 1,
      title: 'Delete',
      icon: 'fa-trash',
      class: 'btn-negative',
        handler(row) {
        const vid = row.get(field);
        const apiUrl = `${that.url}/${deleteUrl}/${vid}/`;
        that.$dialog.confirm('Are you sure you want to delete this record?', 'Delete', async () => {
          try {
            await api.delete(apiUrl);
            // eslint-disable-next-line no-underscore-dangle
            const table_ = that.$refs[refName];
            const table = table_ ? table_.$refs[table_?.name] : null;
            if (table) {
              // eslint-disable-next-line max-len
              table.selectedRows = table.selectedRows.filter((e) => e !== vid);
            }
          } catch ({ status }) {
            if (status !== 200) {
              that.$dialog.alert(
                `Something went wrong when trying to delete the record. Received status: ${status}`
              );
            } else {
              that.$dialog.alert('Record deleted successfully', 'Success');
            }
          } finally {
            // refresh the table
            const table = that.$refs[refName];
            if (table) {
              const store = table.$refs[table?.name]?.store;
              if (store) store.load(store.lastParams);
            }
          }
        });
      },
    });
  }
  return arr;
}

export const AssocPatchCPEListCols = [
  {
    id: 'assoc_id',
    field: 'ID',
    db_field: 'id',
    data: 'assoc_id',
    heading: 'ID',
    canSearch: true,
  },
  {
    id: 'product',
    field: 'Product',
    db_field: 'cpe.product',
    data: 'product',
    heading: 'Product',
    noSort: true,
  },
  {
    id: 'vendor',
    field: 'Vendor',
    db_field: 'cpe.vendor',
    data: 'vendor',
    heading: 'Vendor',
  },
  {
    id: 'version',
    field: 'Version',
    db_field: 'cpe.version',
    data: 'version',
    heading: 'Version',
  },
  {
    id: 'cpe_id',
    field: 'CPE ID',
    db_field: 'cpe.id',
    data: 'cpe_id',
    heading: 'CPE ID',
    canSearch: true,
  },
  {
    id: 'patch_title',
    field: 'Patch',
    db_field: 'patch.title',
    data: 'patch_title',
    heading: 'Patch',
    canSearch: true,
  },
  {
    id: 'patch_id',
    field: 'Patch ID',
    db_field: 'patch_id',
    data: 'patch_id',
    heading: 'Patch ID',
    canSearch: true,
  },
  {
    id: 'severity',
    field: 'Severity',
    db_field: 'severity',
    data: 'severity',
    heading: 'Severity',
    canSearch: true,
  },
];

export const AssocRawPatchCPEListCols = [
  {
    id: 'assoc_id',
    field: 'ID',
    db_field: 'id',
    data: 'assoc_id',
    heading: 'ID',
    canSearch: true,
  },
  {
    id: 'product',
    field: 'Product',
    db_field: 'cpe.product',
    data: 'product',
    heading: 'Product',
    noSort: true,
  },
  {
    id: 'vendor',
    field: 'Vendor',
    db_field: 'cpe.vendor',
    data: 'vendor',
    heading: 'Vendor',
  },
  {
    id: 'version',
    field: 'Version',
    db_field: 'cpe.version',
    data: 'version',
    heading: 'Version',
  },
  {
    id: 'cpe_id',
    field: 'CPE ID',
    db_field: 'cpe.id',
    data: 'cpe_id',
    heading: 'CPE ID',
    canSearch: true,
  },
  {
    id: 'raw_patch_title',
    field: 'Patch',
    db_field: 'raw_patch_title',
    data: 'raw_patch_title',
    heading: 'Raw Patch',
    canSearch: true,
  },
  {
    id: 'raw_patch_id',
    field: 'Raw Patch ID',
    db_field: 'raw_patch_id',
    data: 'raw_patch_id',
    heading: 'Raw Patch ID',
    canSearch: true,
  },
  {
    id: 'severity',
    field: 'Severity',
    db_field: 'severity',
    data: 'severity',
    heading: 'Severity',
    canSearch: true,
  },
];

export const AssocOrgPatchCPEListCols = [
  {
    id: 'assoc_id',
    field: 'ID',
    db_field: 'id',
    data: 'assoc_id',
    heading: 'ID',
    canSearch: true,
  },
  {
    id: 'product',
    field: 'Product',
    db_field: 'cpe.product',
    data: 'product',
    heading: 'Product',
    noSort: true,
  },
  {
    id: 'vendor',
    field: 'Vendor',
    db_field: 'cpe.vendor',
    data: 'vendor',
    heading: 'Vendor',
  },
  {
    id: 'version',
    field: 'Version',
    db_field: 'cpe.version',
    data: 'version',
    heading: 'Version',
  },
  {
    id: 'cpe_id',
    field: 'CPE ID',
    db_field: 'cpe.id',
    data: 'cpe_id',
    heading: 'CPE ID',
    canSearch: true,
  },
  {
    id: 'org_patch_title',
    field: 'Org Patch',
    db_field: 'org_patch_title',
    data: 'org_patch_title',
    heading: 'Org Patch',
    canSearch: true,
  },
  {
    id: 'org_patch_id',
    field: 'Org Patch ID',
    db_field: 'org_patch_id',
    data: 'org_patch_id',
    heading: 'Org Patch ID',
    canSearch: true,
  },
  {
    id: 'severity',
    field: 'Severity',
    db_field: 'severity',
    data: 'severity',
    heading: 'Severity',
    canSearch: true,
  },
];

export const AssocAffectedVulnListCols = [
  {
    id: 'assoc_id',
    field: 'ID',
    db_field: 'id',
    data: 'assoc_id',
    heading: 'ID',
    canSearch: true,
  },
  {
    id: 'product',
    field: 'Product',
    db_field: 'cpe.product',
    data: 'product',
    heading: 'Product',
    noSort: true,
  },
  {
    id: 'vendor',
    field: 'Vendor',
    db_field: 'cpe.vendor',
    data: 'vendor',
    heading: 'Vendor',
    noSort: true,
  },
  {
    id: 'version',
    field: 'Version',
    db_field: 'cpe.version',
    data: 'version',
    heading: 'Version',
    noSort: true,
  },
  {
    id: 'cpe_id',
    field: 'CPE ID',
    db_field: 'cpe.id',
    data: 'cpe_id',
    heading: 'CPE ID',
    canSearch: true,
    noSort: true,
  },
  {
    id: 'vuln_title',
    field: 'Vuln',
    db_field: 'vuln.title',
    data: 'vuln_title',
    heading: 'Vuln',
    canSearch: true,
    noSort: true,
  },
  {
    id: 'first',
    field: 'First',
    db_field: 'first',
    data: 'first',
    heading: 'First',
    canSearch: true,
    noSort: true,
  },
  {
    id: 'last',
    field: 'Last',
    db_field: 'last',
    data: 'last',
    heading: 'Last',
    canSearch: true,
    noSort: true,
  },
];

export const AssocFixedVulnListCols = [
  {
    id: 'assoc_id',
    field: 'ID',
    db_field: 'id',
    data: 'assoc_id',
    heading: 'ID',
    canSearch: true,
  },
  {
    id: 'product',
    field: 'Product',
    db_field: 'cpe.product',
    data: 'product',
    heading: 'Product',
    noSort: true,
  },
  {
    id: 'vendor',
    field: 'Vendor',
    db_field: 'cpe.vendor',
    data: 'vendor',
    heading: 'Vendor',
  },
  {
    id: 'version',
    field: 'Version',
    db_field: 'cpe.version',
    data: 'version',
    heading: 'Version',
  },
  {
    id: 'cpe_id',
    field: 'CPE ID',
    db_field: 'cpe.id',
    data: 'cpe_id',
    heading: 'CPE ID',
    canSearch: true,
  },
  {
    id: 'vuln_title',
    field: 'Vuln',
    db_field: 'vuln.title',
    data: 'vuln_title',
    heading: 'Vuln',
    canSearch: true,
  },
  {
    id: 'recommended',
    field: 'Recommended',
    db_field: 'recommended',
    data: 'recommended',
    heading: 'Recommended',
    canSearch: true,
  },
];

export const FeedCols = [
  {
    id: 'id',
    field: 'ID',
    db_field: 'id',
    data: 'id',
    heading: 'ID',
    canSearch: true,
  },
  {
    id: 'name',
    field: 'Name',
    db_field: 'name',
    data: 'name',
    heading: 'Name',
  },
  {
    id: 'description',
    field: 'Description',
    db_field: 'description',
    data: 'description',
    heading: 'Description',
  },
  {
    id: 'status',
    field: 'Status',
    db_field: 'status',
    data: 'status',
    heading: 'Status',
  },
  {
    id: 'last_started',
    field: 'Last Started',
    db_field: 'last_started',
    data: 'last_started',
    heading: 'Last Started',
    canSearch: true,
  },
  {
    id: 'finished',
    field: 'Last Finished',
    db_field: 'last_finished',
    data: 'last_finished',
    heading: 'Last Finished',
    canSearch: true,
  },
  {
    id: 'last_successful_started',
    field: 'Last Successful Started',
    db_field: 'last_successful_started',
    data: 'last_successful_started',
    heading: 'Last Successful Started',
    canSearch: true,
  },
  {
    id: 'last_successful_finished',
    field: 'Last Successful Finished',
    db_field: 'last_successful_finished',
    data: 'last_successful_finished',
    heading: 'Last Successful Finished',
    canSearch: true,
  },
];

export const KVSCols = [
  {
    id: 'id',
    field: 'ID',
    db_field: 'id',
    data: 'db_id',
    heading: 'ID',
    canSearch: true,
  },
  {
    id: 'vendor',
    field: 'Vendor',
    db_field: 'vendor',
    data: 'vendor',
    heading: 'Vendor',
  },
  {
    id: 'product',
    field: 'Product',
    db_field: 'product',
    data: 'product',
    heading: 'Product',
  },
  {
    id: 'cycle_decimals',
    field: 'Cycle Decimals',
    db_field: 'cycle_decimals',
    data: 'cycle_decimals',
    heading: 'Cycle Decimals',
  },
  {
    id: 'stream_cycle',
    field: 'Stream Cycle',
    db_field: 'stream_cycle',
    data: 'stream_cycle',
    heading: 'Stream Cycle',
  },
];

export const FVSCols = [
  {
    id: 'id',
    field: 'ID',
    db_field: 'id',
    data: 'db_id',
    heading: 'ID',
    canSearch: true,
  },
  {
    id: 'vendor',
    field: 'Vendor',
    db_field: 'vendor',
    data: 'vendor',
    heading: 'Vendor',
  },
  {
    id: 'product',
    field: 'Product',
    db_field: 'product',
    data: 'product',
    heading: 'Product',
  },
  {
    id: 'cycle_decimals',
    field: 'Cycle Decimals',
    db_field: 'cycle_decimals',
    data: 'cycle_decimals',
    heading: 'Cycle Decimals',
  },
];

export const EPMCols = [
  {
    id: 'id',
    field: 'ID',
    db_field: 'id',
    data: 'db_id',
    heading: 'ID',
    canSearch: true,
  },
  {
    id: 'eol_product',
    field: 'Eol Product',
    db_field: 'eol_product',
    data: 'eol_product',
    heading: 'Eol Product',
  },
  {
    id: 'apck_product',
    field: 'Apck Product',
    db_field: 'apck_product',
    data: 'apck_product',
    heading: 'Apck Product',
  },
  {
    id: 'apck_vendor',
    field: 'Apck Vendor',
    db_field: 'apck_vendor',
    data: 'apck_vendor',
    heading: 'Apck Vendor',
  },
  {
    id: 'fuzzy_version_stream_decimals',
    field: 'Fuzzy Version Stream Decimals',
    db_field: 'fuzzy_version_stream_decimals',
    data: 'fuzzy_version_stream_decimals',
    heading: 'Fuzzy Version Stream Decimals',
  },
];

export const EOLCols = [
  {
    id: 'id',
    field: 'ID',
    db_field: 'id',
    data: 'db_id',
    heading: 'ID',
    canSearch: true,
  },
  {
    id: 'source_id',
    field: 'Source ID',
    db_field: 'source_id',
    data: 'source_id',
    heading: 'Source ID',
  },
  {
    id: 'feed_id',
    field: 'Feed ID',
    db_field: 'feed_id',
    data: 'feed_id',
    heading: 'Feed ID',
  },
  {
    id: 'release_date',
    field: 'Release Date',
    db_field: 'release_date',
    data: 'release_date',
    heading: 'Release Date',
  },
  {
    id: 'latest',
    field: 'Latest',
    db_field: 'latest',
    data: 'latest',
    heading: 'Latest',
  },
  {
    id: 'cycle',
    field: 'Cycle',
    db_field: 'cycle',
    data: 'cycle',
    heading: 'Cycle',
  },
  {
    id: 'details',
    field: 'Details',
    db_field: 'details',
    data: 'details',
    heading: 'Details',
  },
  {
    id: 'eol_product',
    field: 'Eol Product',
    db_field: 'eol_product',
    data: 'eol_product',
    heading: 'Eol Product',
  },
  {
    id: 'apck_product',
    field: 'Apck Product',
    db_field: 'apck_product',
    data: 'apck_product',
    heading: 'Apck Product',
  },
  {
    id: 'apck_vendor',
    field: 'Apck Vendor',
    db_field: 'apck_vendor',
    data: 'apck_vendor',
    heading: 'Apck Vendor',
  },
  {
    id: 'eol',
    field: 'Eol',
    db_field: 'eol',
    data: 'eol',
    heading: 'Eol',
  },
  {
    id: 'eol_bool',
    field: 'Eol Bool',
    db_field: 'eol_bool',
    data: 'eol_bool',
    heading: 'Eol Bool',
  },
  {
    id: 'support',
    field: 'Support',
    db_field: 'support',
    data: 'support',
    heading: 'Support',
  },
  {
    id: 'support_bool',
    field: 'Support Bool',
    db_field: 'support_bool',
    data: 'support_bool',
    heading: 'Support Bool',
  },
  {
    id: 'lts',
    field: 'Lts',
    db_field: 'lts',
    data: 'lts',
    heading: 'Lts',
  },
  {
    id: 'lts_bool',
    field: 'Lts Bool',
    db_field: 'lts_bool',
    data: 'lts_bool',
    heading: 'Lts Bool',
  },
];

export const KEVCols = [
  {
    id: 'id',
    field: 'ID',
    db_field: 'id',
    data: 'db_id',
    heading: 'ID',
    canSearch: true,
  },
  {
    id: 'name',
    field: 'Name',
    db_field: 'name',
    data: 'name',
    heading: 'Name',
  },
  {
    id: 'description',
    field: 'Description',
    db_field: 'description',
    data: 'description',
    heading: 'Description',
  },
  {
    id: 'cve',
    field: 'CVE',
    db_field: 'cve',
    data: 'cve',
    heading: 'CVE',
  },
  {
    id: 'vendor',
    field: 'Vendor',
    db_field: 'vendor',
    data: 'vendor',
    heading: 'Vendor',
  },
  {
    id: 'product',
    field: 'Product',
    db_field: 'product',
    data: 'product',
    heading: 'Product',
  },
  // {
  //     id: 'action',
  //     field: 'Action',
  //     db_field: 'action',
  //     data: 'action',
  //     heading: 'Action',
  // },
  {
    id: 'due_date',
    field: 'Due Date',
    db_field: 'due_date',
    data: 'due_date',
    heading: 'Due Date',
    canSearch: true,
  },
  {
    id: 'date_added',
    field: 'date_added',
    db_field: 'date_added',
    data: 'date_added',
    heading: 'Date Added',
    canSearch: true,
  },
  {
    id: 'first_known_exploitation_date',
    field: 'first_known_exploitation_date',
    db_field: 'first_known_exploitation_date',
    data: 'first_known_exploitation_date',
    heading: 'First Known Exploitation Date',
    canSearch: true,
  },
];

export const VSCols = [
  {
    id: 'id',
    field: 'ID',
    db_field: 'id',
    data: 'db_id',
    heading: 'ID',
    canSearch: true,
  },
  {
    id: 'name',
    field: 'Name',
    db_field: 'name',
    data: 'name',
    heading: 'Name',
  },
  {
    id: 'identifier',
    field: 'Identifier',
    db_field: 'identifier',
    data: 'identifier',
    heading: 'Identifier',
  },
  {
    id: 'detected_by_scanner',
    field: 'Detected',
    db_field: 'detected_by_scanner',
    data: 'detected_by_scanner',
    heading: 'Detected',
  },
];

export const DIRCols = [
  {
    id: 'id',
    field: 'ID',
    db_field: 'id',
    data: 'id',
    heading: 'ID',
    canSearch: true,
  },
  {
    id: 'vendor',
    field: 'Vendor',
    db_field: 'vendor',
    data: 'vendor',
    heading: 'Vendor',
  },
  {
    id: 'display_info_required',
    field: 'Display Info Required',
    db_field: 'display_info_required',
    data: 'display_info_required',
    heading: 'Display Info Required',
  },
  {
    id: 'logo_required',
    field: 'Logo Required',
    db_field: 'logo_required',
    data: 'logo_required',
    heading: 'Logo Required',
  },
];

export const CipherSuiteCols = [
  {
    id: 'id',
    field: 'ID',
    db_field: 'id',
    data: 'db_id',
    heading: 'ID',
    width: 120,
  },
  {
    id: 'title',
    field: 'Title',
    db_field: 'title',
    data: 'title',
    heading: 'Title',
  },
  {
    id: 'gnutls_name',
    field: 'Gnutls Name',
    db_field: 'gnutls_name',
    data: 'gnutls_name',
    heading: 'Gnutls Name',
  },
  {
    id: 'openssl_name',
    field: 'Openssl Name',
    db_field: 'openssl_name',
    data: 'openssl_name',
    heading: 'Openssl Name',
  },
  {
    id: 'hex_byte_1',
    field: 'Hex Byte 1',
    db_field: 'hex_byte_1',
    data: 'hex_byte_1',
    heading: 'Hex Byte 1',
  },
  {
    id: 'hex_byte_2',
    field: 'Hex Byte 2',
    db_field: 'hex_byte_2',
    data: 'hex_byte_2',
    heading: 'Hex Byte 2',
  },
  {
    id: 'protocol_version',
    field: 'Protocol Version',
    db_field: 'protocol_version',
    data: 'protocol_version',
    heading: 'Protocol Version',
  },
  {
    id: 'kex_algorithm',
    field: 'Kex Algorithm',
    db_field: 'kex_algorithm',
    data: 'kex_algorithm',
    heading: 'Kex Algorithm',
  },
  {
    id: 'auth_algorithm',
    field: 'Auth Algorithm',
    db_field: 'auth_algorithm',
    data: 'auth_algorithm',
    heading: 'Auth Algorithm',
  },
  {
    id: 'enc_algorithm',
    field: 'Enc Algorithm',
    db_field: 'enc_algorithm',
    data: 'enc_algorithm',
    heading: 'Enc Algorithm',
  },
  {
    id: 'hash_algorithm',
    field: 'Hash Algorithm',
    db_field: 'hash_algorithm',
    data: 'hash_algorithm',
    heading: 'Hash Algorithm',
  },
  {
    id: 'security',
    field: 'Security',
    db_field: 'security',
    data: 'security',
    heading: 'Security',
  },
  {
    id: 'tls_version',
    field: 'TLS Version',
    db_field: 'tls_version',
    data: 'tls_version',
    heading: 'TLS Version',
  },
];

export const VulnImportFilterCols = [
  {
    id: 'id',
    field: 'ID',
    db_field: 'id',
    data: 'db_id',
    heading: 'ID',
    width: 120,
  },
  {
    id: 'name',
    field: 'Name',
    db_field: 'name',
    data: 'name',
    heading: 'Name',
    width: 150,
  },
  {
    id: 'feed_id',
    field: 'Feed',
    db_field: 'feed.name',
    data: 'feed.name',
    heading: 'Feed Name',
    width: 150,
  },
  {
    id: 'conditions',
    field: 'Conditions',
    db_field: 'conditions',
    data: 'conditions',
    heading: 'Conditions',
  },
  {
    id: 'filter_type',
    field: 'Filter Type',
    db_field: 'filter_type',
    data: 'filter_type',
    heading: 'Filter Type',
  },
];

export const SecurityAlertCols = [
  {
    id: 'id',
    field: 'ID',
    db_field: 'id',
    data: 'db_id',
    heading: 'ID',
    width: 120,
  },
  {
    id: 'title',
    field: 'Title',
    db_field: 'title',
    data: 'title',
    heading: 'Title',
  },
  {
    id: 'severity',
    field: 'Severity',
    db_field: 'severity',
    data: 'severity',
    heading: 'Severity',
  },
  {
    id: 'description',
    field: 'Description',
    db_field: 'description',
    data: 'description',
    heading: 'Description',
  },
];

export const CPEMetricCols = [
  {
    id: 'id',
    field: 'ID',
    db_field: 'id',
    data: 'db_id',
    heading: 'ID',
    canSearch: true,
  },
  {
    id: 'vendor',
    field: 'Vendor',
    db_field: 'vendor',
    data: 'vendor',
    heading: 'Vendor',
  },
  {
    id: 'product',
    field: 'Product',
    db_field: 'product',
    data: 'product',
    heading: 'Product',
  },
  {
    id: 'version',
    field: 'Version',
    db_field: 'version',
    data: 'version',
    heading: 'Version',
  },
  {
    id: 'date',
    field: 'Date',
    db_field: 'date',
    data: 'date',
    heading: 'Date',
  },
  {
    id: 'count_vulns',
    field: 'Count Vulns',
    db_field: 'count_vulns',
    data: 'count_vulns',
    heading: 'Count Vulns',
  },
  {
    id: 'count_check_latest_version',
    field: 'Count Check Latest Version',
    db_field: 'count_check_latest_version',
    data: 'count_check_latest_version',
    heading: 'Count Check Latest Version',
  },
];

export const VulnMetricCols = [
  {
    id: 'id',
    field: 'ID',
    db_field: 'id',
    data: 'db_id',
    heading: 'ID',
    canSearch: true,
  },
  {
    id: 'date',
    field: 'Date',
    db_field: 'date',
    data: 'date',
    heading: 'Date',
  },
  {
    id: 'count',
    field: 'Count',
    db_field: 'count',
    data: 'count',
    heading: 'Count',
  },
  {
    id: 'vuln_id',
    field: 'Vuln ID',
    db_field: 'vuln_id',
    data: 'vuln_id',
    heading: 'Vuln ID',
  },
  {
    id: 'vuln_title',
    field: 'Title',
    db_field: 'vuln.title',
    data: 'vuln.title',
    heading: 'Vuln',
    canSearch: true,
    noSort: true,
  },
  {
    id: 'vuln_workflow_quality',
    field: 'Workflow Quality',
    db_field: 'vuln.workflow_quality',
    data: 'vuln.workflow_quality',
    heading: 'Workflow Quality',
    canSearch: true,
    noSort: true,
  },
];

export const FullPatchCols = [
  {
    id: 'id',
    field: 'ID',
    db_field: 'id',
    data: 'id',
    heading: 'ID',
    width: 100,
  },
  {
    id: 'release_date',
    field: 'Date',
    db_field: 'release_date',
    data: 'release_date',
    heading: 'Date',
    width: 140,
  },
  {
    id: 'description',
    field: 'Description',
    db_field: 'description',
    data: 'description',
    heading: 'Description',
  },
  {
    id: 'patch_id',
    field: 'Patch ID',
    db_field: 'patch_id',
    data: 'patch_id',
    heading: 'Patch ID',
  },
  {
    id: 'changes',
    field: 'Changes',
    db_field: 'changes',
    data: 'changes',
    heading: 'Changes',
  },
  {
    id: 'issues',
    field: 'Issues',
    db_field: 'issues',
    data: 'issues',
    heading: 'Issues',
  },
  {
    id: 'patch_link',
    field: 'Patch Link',
    db_field: 'patch_link',
    data: 'patch_link',
    heading: 'Patch Link',
  },
];

export const CPEPriorityCols = [
  {
    id: 'id',
    field: 'ID',
    db_field: 'id',
    data: 'db_id',
    heading: 'ID',
    canSearch: true,
  },
  {
    id: 'vendor',
    field: 'Vendor',
    db_field: 'vendor',
    data: 'vendor',
    heading: 'Vendor',
  },
  {
    id: 'product',
    field: 'Product',
    db_field: 'product',
    data: 'product',
    heading: 'Product',
  },
  {
    id: 'authenticated_detection',
    field: 'authenticated_detection',
    db_field: 'authenticated_detection',
    data: 'authenticated_detection',
    heading: 'Authenticated Detection',
  },
  {
    id: 'unauthenticated_detection',
    field: 'unauthenticated_detection',
    db_field: 'unauthenticated_detection',
    data: 'unauthenticated_detection',
    heading: 'Unauthenticated Detection',
  },
  {
    id: 'customer_popularity',
    field: 'Customer Popularity',
    db_field: 'customer_popularity',
    data: 'customer_popularity',
    heading: 'Customer Popularity',
  },
  {
    id: 'global_popularity',
    field: 'Global Popularity',
    db_field: 'global_popularity',
    data: 'global_popularity',
    heading: 'Global Popularity',
  },
  {
    id: 'intuition',
    field: 'Intuition',
    db_field: 'intuition',
    data: 'intuition',
    heading: 'Intuition',
  },
  {
    id: 'priority',
    field: 'Priority',
    db_field: 'priority',
    data: 'priority',
    heading: 'Priority',
  },
];

export const LogoCols = [
  {
    id: 'id',
    field: 'ID',
    db_field: 'id',
    data: 'db_id',
    heading: 'ID',
    canSearch: true,
  },
  {
    id: 'name',
    field: 'Name',
    db_field: 'name',
    data: 'name',
    heading: 'Name',
  },
  {
    id: 'note',
    field: 'Note',
    db_field: 'note',
    data: 'note',
    heading: 'Note',
  },
  {
    id: 'logo',
    field: 'Logo',
    db_field: 'logo',
    heading: 'Logo',
    html: true,
    data: formatImgColumn('logo', 'content_type', 'Logo thumbnail'),
  },
  {
    id: 'format',
    field: 'Type',
    db_field: 'format',
    heading: 'Type',
  },
  {
    id: 'resolution',
    field: 'Resolution',
    db_field: 'resolution',
    heading: 'Resolution',
    data: ({ width, height }) => (width > 0 && height > 0 ? `${width}x${height}` : '-'),
  },
];

export const DisplayInfoCols = [
  {
    id: 'id',
    field: 'ID',
    db_field: 'id',
    data: 'db_id',
    heading: 'ID',
    canSearch: true,
  },
  {
    id: 'vendor',
    field: 'Vendor',
    db_field: 'vendor',
    data: 'vendor',
    heading: 'Vendor',
  },
  {
    id: 'product',
    field: 'Product',
    db_field: 'product',
    data: 'product',
    heading: 'Product',
  },
  {
    id: 'update_str',
    field: 'Update',
    db_field: 'update_str',
    data: 'update_str',
    heading: 'Update',
  },
  {
    id: 'display_name',
    field: 'Display Name',
    db_field: 'display_name',
    heading: 'Display Name',
    data: 'display_name',
  },
  {
    id: 'created_on',
    field: 'Created On',
    db_field: 'created_on',
    heading: 'Created On',
    data: 'created_on',
  },
  {
    id: 'changed_on',
    field: 'Changed On',
    db_field: 'changed_on',
    heading: 'Changed On',
    data: 'changed_on',
  },
  {
    id: 'logo',
    field: 'Logo',
    db_field: 'logo',
    heading: 'Logo',
    html: true,
    data: formatImgColumn('logo.logo', 'logo.content_type', 'Logo thumbnail'),
  },
  {
    id: 'logo_resolution',
    field: 'Resolution',
    db_field: 'logo_resolution',
    heading: 'Resolution',
    data: ({ logo }) =>
      logo?.width > 0 && logo?.height > 0 ? `${logo.width}x${logo.height}` : '-',
  },
];

export const APIClientCols = [
  {
    id: 'id',
    field: 'ID',
    db_field: 'id',
    data: 'id',
    heading: 'ID',
    width: 80,
  },
  {
    id: 'client_id',
    field: 'Client ID',
    db_field: 'client_id',
    data: 'client_id',
    heading: 'Client ID',
  },
  {
    id: 'scopes',
    field: 'Scopes',
    db_field: 'scopes',
    data: 'scopes',
    heading: 'Scopes',
  },
  {
    id: 'description',
    field: 'Description',
    db_field: 'description',
    data: 'description',
    heading: 'Description',
  },
  {
    id: 'is_active',
    field: 'Active',
    db_field: 'is_active',
    data: 'is_active',
    heading: 'Active',
    width: 80,
  },
  {
    id: 'created_on',
    field: 'Created',
    db_field: 'created_on',
    data: 'created_on',
    heading: 'Created',
    width: 160,
  },
  {
    id: 'changed_on',
    field: 'Changed',
    db_field: 'changed_on',
    data: 'changed_on',
    heading: 'Changed',
    width: 160,
  },
  {
    id: 'last_used_on',
    field: 'Last Used',
    db_field: 'last_used_on',
    data: 'last_used_on',
    heading: 'Last Used',
    width: 160,
  },
];

export function baseLoadSavedQuery(that) {
  let sort;
  let page;
  let page_size;
  let rawFilters;
  const table = that.$refs[that.name];
  const query = that.$route?.query;
  try {
    // load in filters url param
    // rawFilters are the list of filters in the filter bar form.
    // these are later parsed into the backend compatible format.
    rawFilters = query.filters ? JSON.parse(query.filters) : [];
    // load in url sort params
    sort = query?.sort;
    page = query?.page;
    page_size = query?.page_size;
    if (sort) table.store.sort = sort;
    if (page) table.store.page = page;
    if (page_size) table.store.limit = page_size;
    sort = sort ? JSON.parse(sort) : [];
    // the table store keeps track of the stringified sort parameters.
    // the actual table stores the list of sort params itself.
    // eslint-disable-next-line no-param-reassign
    that.sort = sort;
    that.page = page;
    that.page_size = page_size;
    that.sort.forEach((e) => {
      const col = that.cols.find((v) => e.field === v.db_field);
      if (col) col.direction = e.direction;
    });
    // eslint-disable-next-line no-param-reassign
    that.filterSearchValue = rawFilters;
    const { filters: parsedFilters, page: _, ...rest } = that.parseFilters(rawFilters);
    table.store.filters = parsedFilters;
    that.reloadWithParam({
      filters: JSON.stringify(table.store.filters),
      sort: table.store.sort,
      page,
      page_size,
      ...rest,
    });
    if (page) table.store.setterPage(page);
  } catch (e) {
    // on ill-formed input
    that.reloadWithParam({});
  }
}

export const baseTableProps = Object.freeze({
  title: 'Apck Table',
  method: 'get',
  src: '',
  limit: 25,
  actions: [],
  hideSort: true,
  useHeaderComponent: true,
  showBar: true,
  parseFilters: (e) => e,
  showApckSearch: true,
  autoload: true,
  usePageSize: true,
  rowIdentifier: 'db_id',
  disableSelectedRow: true,
  pageResetValue: 1,
});

export function impactBadgeColour(v) {
  if (!v) return 'badge-none';
  if (v.includes('unknown')) return 'badge-unknown';
  if (v.includes('investigate')) return 'badge-investigate';
  if (v.includes('info')) return 'badge-info';
  if (v.includes('low')) return 'badge-low';
  if (v.includes('medium')) return 'badge-medium';
  if (v.includes('high')) return 'badge-high';
  return 'badge-critical';
}

export function probabilityBadgeColour(v) {
  if (!v) return 'badge-none';
  if (v.includes('unknown')) return 'badge-unknown';
  if (v.includes('investigate')) return 'badge-investigate';
  if (v.includes('info')) return 'badge-info';
  if (v.includes('low')) return 'badge-low';
  if (v.includes('medium')) return 'badge-medium';
  if (v.includes('high')) return 'badge-high';
  return 'badge-critical';
}

export function impactData(v) {
  return `<span class="badge text-std ${impactBadgeColour(v?.impact ?? '1.0-low')}"
    style="white-space: pre-line">${IMPACT_PROBABILITY_READ_VALUES[v?.impact] ?? '-'}</span>`;
}

export function probabilityData(v) {
  return `<span class="badge text-std ${probabilityBadgeColour(v?.probability ?? '1.0-low')}"
    style="white-space: pre-line">${IMPACT_PROBABILITY_READ_VALUES[v?.probability] ?? '-'}</span>`;
}

export function deleteSelected(apiUrl, table, url, noReload, successFn, full_delete) {
  const keyword = !full_delete && url.includes('vulns/delete') ? 'deprecate' : 'delete';
  this.$dialog.confirm(
    `Are you sure you want to ${keyword} the selected items?`,
    `${keyword.charAt(0).toUpperCase() + keyword.slice(1)} Items`,
    async () => {
      const selected = table.selectedRows;
      try {
        const r = await api.post(`${apiUrl}/${url}/`, { db_ids: selected, full_delete });
        if (!r.ok) throw new Error(await r.json());
        const sData = await r.json();
        const { msg } = sData;
        toast({
          message: msg ?? `Selected rows have been ${keyword}d successfully!`,
          type: 'success',
        });
        // update selections
        table.selectedRows = [];
        if (successFn) successFn(sData);
      } catch (e) {
        // eslint-disable-next-line camelcase
        let errorMsg = `Something went wrong when trying to ${keyword} selected rows.`;
        const { msg, correlation_id } = e ?? { msg: errorMsg, correlation_id: '-' };
        toast({
          // eslint-disable-next-line camelcase
          message:
            `${msg}\nCorrelation ID: ${correlation_id}` ??
            `Something went wrong when trying to ${keyword} the selected rows.`,
          type: 'danger',
        });
      } finally {
        if (!noReload && table?.store) table.store.load(table.store.lastParams);
      }
    }
  );
}

export function approveSelected(apiUrl, table, url, noReload, successFn, clear, override) {
  this.$dialog.confirm(
    `Are you sure you want to approve the CPEs on the selected items?`,
    `Confirm CPEs`,
    async () => {
      const selected = table.selectedRows;
      try {
          const r = await api.post(`${apiUrl}/${url}/`, { db_ids: selected, clear, override });
        if (!r.ok) throw new Error(await r.json());
        const sData = await r.json();
        const { msg } = sData;
        toast({
          message: msg ?? 'Selected rows have been processed successfully!',
          type: 'success',
        });
        // update selections
        table.selectedRows = [];
        if (successFn) successFn(sData);
      } catch (e) {
        // eslint-disable-next-line camelcase
        let errorMsg = `Something went wrong when trying to confirm CPEs for selected rows.`;
        const { msg, correlation_id } = e ?? { msg: errorMsg, correlation_id: '-' };
        toast({
          // eslint-disable-next-line camelcase
          message:
            `${msg}\nCorrelation ID: ${correlation_id}` ??
            `Something went wrong when trying to confirm CPEs for selected rows.`,
          type: 'danger',
        });
      } finally {
        if (!noReload && table?.store) table.store.load(table.store.lastParams);
      }
    }
  );
}


export function regenerateSelected(apiUrl, table, url, noReload, successFn) {
  this.$dialog.confirm(
    'Are you sure you want to regenerate these Vulns? DANGER: This will overwrite any and all manual edits that have been made to these Vulns.',
    'Regenerate Items',
    async () => {
      const selected = table.selectedRows;
      const data = JSON.stringify({ db_ids: selected });
      try {
        const r = await api.post(`${apiUrl}/${url}/`, { db_ids: selected, full_delete });
        if (!r.ok) throw new Error(await r.json());
        const sData = await r.json();
        toast({
          message: msg ?? 'Selected rows have been regenerated successfully!',
          type: 'success',
        });
        // update selections
        table.selectedRows = [];
        if (successFn) successFn(sData);
      } catch (e) {
        // eslint-disable-next-line camelcase
        let errorMsg = 'Something went wrong when regenerating selected rows.';
        const { msg, correlation_id } = e ?? { msg: errorMsg, correlation_id: '-' };
        let error_details = '';
        for (const [k, v] of Object.entries(error.errors)) {
          error_details += `\nVuln: ${k} error: ${v}`;
        }
        toast({
          // eslint-disable-next-line camelcase
          message: `Correlation ID: ${correlation_id}${error_details}`,
          type: 'danger',
        });
      } finally {
        if (!noReload && table?.store) table.store.load(table.store.lastParams);
      }
    }
  );
}

export function isNumeric(num) {
  return (
    (typeof num === 'number' || (typeof num === 'string' && num.trim() !== '')) &&
    // eslint-disable-next-line no-restricted-globals
    !isNaN(num)
  );
}

export function groupFilters(filters) {
  // group the same filters and put them into OR clauses
  // this is a potentially temporary workaround
  const filterMap = groupBy(filters, 'field');
  return Object.keys(filterMap).map((key) => {
    if (filterMap[key].length !== 1) {
      return {
        or: [filterMap[key]],
      };
    }
    return filterMap[key][0];
  });
}

export function filterSuggestionsOnly(filters) {
  if (filters == undefined) {
    filters = [];
  }
  filters = filters.filter(function (filter) {
    return filter.field != 'suggestions';
  });
  filters.push({
    field: 'suggestions',
    op: 'eq',
    value: 'true',
  });
  return filters;
}

export function formatImg(obj = {}) {
  if (obj === null || obj === undefined) return '';
  const { logo = null, content_type = 'image/jpeg' } = obj;
  if (!logo) return '';
  // if it's already processed we return early.
  if (logo.startsWith('data:')) return logo;
  // default to image/jpeg
  const src = `data:${content_type || 'image/jpeg'};charset=utf-8;base64,${logo}`;
  return src ?? '';
}

const WORKFLOW_QUALITY_MAPPING = {
  0: 'Not Implemented',
  10: 'Terrible',
  20: 'Bad',
  30: 'Ok',
  40: 'Good',
  50: 'Great',
};

function generateDates(startDate, endDate) {
  const dates = [];
  const currentDate = new Date(startDate.getTime());

  while (currentDate <= endDate) {
    dates.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return dates;
}

export function fetchQualityKpi(url, opts, chartData) {
  chartData.loading = true;
  AjaxUtils.ajax_get({
    url: `${url}?${new URLSearchParams(chartData.queryParams).toString()}`,
    opts,
    success: (data) => {
      const hAfter = chartData.queryParams.hours_after_creation;
      let title;
      if (url.includes('vuln_quality_over_time_kpi')) {
        title = `Vulnerabilities imported by quality level (${hAfter}h after import)`;
      } else if (url.includes('kev_quality_over_time_kpi')) {
        title = `KEVs imported by quality level (${hAfter}h after import)`;
      }
      if (title) chartData.chartBarOptions.title.text = title;
      const groupedByQuality = groupBy(data.items, 'workflow_quality');
      const minDate = new Date(chartData.queryParams.since);
      const maxDate = new Date(chartData.queryParams.until);
      const dateArray = generateDates(minDate, maxDate);

      const seriesData = Object.entries(groupedByQuality).reduce((prev, [k, v]) => {
        const qualityData = v.reduce((p, e) => {
          const formattedDate = formatDateTime(new Date(e.creation_date), true);
          p[formattedDate] = e.count;
          return p;
        }, {});
        const fullDateRange = dateArray.map((date) => [
          date,
          qualityData[formatDateTime(date, true)] || 0,
        ]);
        const mappedName = WORKFLOW_QUALITY_MAPPING[parseInt(k, 10)];
        prev[mappedName] = fullDateRange;
        return prev;
      }, {});

      const requiredCategories = ['Not Implemented', 'Terrible', 'Bad', 'Ok', 'Good', 'Great'];

      chartData.chartBarSeries = requiredCategories.map((category) => ({
        name: category,
        data: seriesData[category] || dateArray.map((date) => [date, 0]),
      }));
      chartData.chartBarOptions = { ...chartData.chartBarOptions };
      chartData.loading = false;
    },
    fail: (...props) => {
      chartData.loading = false;
    },
  });
}

export function fetchCountKpi(url, opts, chartData) {
  chartData.loading = true;
  AjaxUtils.ajax_get({
    url: `${url}?${new URLSearchParams(chartData.queryParams).toString()}`,
    opts,
    success: (data) => {
      const minDate = new Date(chartData.queryParams.since);
      const maxDate = new Date(chartData.queryParams.until);
      const dateArray = generateDates(minDate, maxDate);

      const r = Object.fromEntries(
        data.items.map((e) => [formatDateTime(new Date(e.creation_date), true), e.count])
      );

      const series = dateArray.map((date) => [date.getTime(), r[formatDateTime(date, true)] || 0]);

      chartData.chartBarSeries = [
        {
          name: 'Entries',
          data: series,
        },
      ];

      chartData.chartBarOptions = { ...chartData.chartBarOptions };
      chartData.loading = false;
    },
    fail: (...props) => {
      chartData.loading = false;
    },
  });
}

export function processCountKPI(d, since, until, name = 'Entries') {
  try {
    const minDate = new Date(since);
    const maxDate = new Date(until);
    const dateArray = generateDates(minDate, maxDate);
    const r = Object.fromEntries(
      d.map((e) => [formatDateTime(new Date(e.creation_date), true), e.count])
    );
    const series = dateArray.map((date) => [date.getTime(), r[formatDateTime(date, true)] || 0]);

    const chartBarSeries = [
      {
        name,
        data: series,
      },
    ];
    return chartBarSeries;
  } catch (e) {
    return { name, data: [] };
  }
}

export function fetchFeedsRanKpi(url, opts, chartData) {
  chartData.loading = true;
  AjaxUtils.ajax_get({
    url: `${url}?${new URLSearchParams(chartData.queryParams).toString()}`,
    opts,
    success: (data) => {
      const minDate = new Date(chartData.queryParams.since);
      const maxDate = new Date(chartData.queryParams.until);
      const dateArray = generateDates(minDate, maxDate);

      const r = Object.fromEntries(
        data.items.map((e) => [e.creation_date, [e.ran, e.successfully_ran]])
      );
      const ran = dateArray.map((e) => {
        const c = r[formatDateTime(e, true)];
        return [e, c ? c[0] : 0];
      });
      const successfully_ran = dateArray.map((e) => {
        const c = r[formatDateTime(e, true)];
        return [e, c ? c[1] : 0];
      });

      const series = [
        {
          name: 'Ran (including failed and successful)',
          data: ran,
          opacity: 1,
        },
        {
          name: 'Successfully Ran',
          data: successfully_ran,
          opacity: 0.6,
        },
      ];
      chartData.chartBarSeries = series;
      chartData.chartBarOptions = { ...chartData.chartBarOptions };
      chartData.loading = false;
    },
    // eslint-disable-next-line
    fail: (...proplets) => {
      chartData.loading = false;
    },
  });
}

export function fetchCpeMetricKpi(url, opts, chartData) {
  chartData.loading = true;
  AjaxUtils.ajax_get({
    url: `${url}?${new URLSearchParams(chartData.queryParams).toString()}`,
    opts,
    success: (data) => {
      const minDate = new Date(chartData.queryParams.since);
      const maxDate = new Date(chartData.queryParams.until);
      const dateArray = generateDates(minDate, maxDate);

      const r = Object.fromEntries(
        data.items.map((e) => [
          formatDateTime(new Date(e.date), true),
          [
            e.total_count_check_latest_version,
            e.total_count_check_end_of_life,
            e.total_count_vulns,
          ],
        ])
      );

      const total_count_check_latest_version = dateArray.map((date) => [
        date,
        r[formatDateTime(date, true)] ? r[formatDateTime(date, true)][0] : 0,
      ]);
      const total_count_check_end_of_life = dateArray.map((date) => [
        date,
        r[formatDateTime(date, true)] ? r[formatDateTime(date, true)][1] : 0,
      ]);
      const total_count_vulns = dateArray.map((date) => [
        date,
        r[formatDateTime(date, true)] ? r[formatDateTime(date, true)][2] : 0,
      ]);

      const series = [
        {
          name: 'Check Latest Version',
          data: total_count_check_latest_version,
          opacity: 1,
        },
        {
          name: 'Check End of Life',
          data: total_count_check_end_of_life,
          opacity: 0.6,
        },
        {
          name: 'Vulns',
          data: total_count_vulns,
          opacity: 0.6,
        },
      ];
      chartData.chartBarSeries = series;
      chartData.chartBarOptions = { ...chartData.chartBarOptions };
      chartData.loading = false;
    },
    // eslint-disable-next-line
    fail: (...proplets) => {
      chartData.loading = false;
    },
  });
}

export const allVulnColumns = [
  { value: 'id', name: 'ID' },
  { value: 'feed.name', name: 'Feed Name' },
  { value: 'workflow_status', name: 'Workflow Status' },
  { value: 'workflow_quality', name: 'Current Workflow Quality' },
  { value: 'target_workflow_quality', name: 'Target Workflow Quality' },
  { value: 'workflow_priority', name: 'Workflow Priority' },
  { value: 'title', name: 'Title' },
  { value: 'description', name: 'Description' },
  { value: 'details', name: 'Details' },
  { value: 'remediation', name: 'Remediation' },
  { value: 'references', name: 'References' },
  { value: 'impact', name: 'Impact' },
  { value: 'probability', name: 'Probability' },
  { value: 'cvss_v2_vector', name: 'CVSS V2 Vector' },
  { value: 'cvss_v2_score', name: 'CVSS V2 Score' },
  { value: 'cvss_v3_vector', name: 'CVSS V3 Vector' },
  { value: 'cvss_v3_score', name: 'CVSS V3 Score' },
  { value: 'cvss_v4_vector', name: 'CVSS V4 Vector' },
  { value: 'cvss_v4_score', name: 'CVSS V4 Score' },
  { value: 'cvss_v2_note', name: 'CVSS V2 Note' },
  { value: 'cvss_v3_note', name: 'CVSS V3 Note' },
  { value: 'cvss_v4_note', name: 'CVSS V4 Note' },
  { value: 'type', name: 'Type' },
  { value: 'category', name: 'Category' },
  { value: 'owasp_2013', name: 'OWASP 2013' },
  { value: 'owasp_2017', name: 'OWASP 2017' },
  { value: 'owasp_2021', name: 'OWASP 2021' },
  { value: 'internal_notes', name: 'Internal Notes' },
  { value: 'changed_on', name: 'Changed On' },
  { value: 'created_on', name: 'Created On' },
  { value: 'forked', name: 'Forked' },
  { value: 'vuln_source.name', name: 'CNA' },
  { value: 'cwes', name: 'CWEs' },
  { value: 'euvds', name: 'EUVDs' },
  { value: 'editing_priority', name: 'Editing Priority' },
];
